import { memoize } from '@grammarly/function-utils'
import { LoggingImpl, Monitoring, Logging, LogEvent } from '@grammarly/telemetry'

import { getConfig } from '../../../config/'
import { redactPiiFromUrl } from '../../utils/pii'
import { gnarClient } from '../gnar'
import { transformToString } from '../../utils/error'
import { BUILD_TAG, GR_ENV, IS_DEPLOYED } from '../../../constants'
import { stubFetch } from '../metrics'

const { tracking } = getConfig()

export const getLogger: (name: string) => Logging.Logger = memoize(Monitoring.Logging.getLogger)

async function addLoggingContext(event: LogEvent): Promise<LogEvent> {
  const baseContext = {
    ...event.context,
    url: redactPiiFromUrl(document.location.href),
    pathname: document.location.pathname,
  }

  if (event.level >= Logging.LogLevel.WARN) {
    try {
      baseContext.containerId = await gnarClient.getContainerId()
    } catch (err) {
      const errMsg = err instanceof Error ? transformToString(err) : 'unknown'
      baseContext.containerId = `Cannot get containerID: ${errMsg}`
    }
  }

  return event.addContext(baseContext)
}

export function configureLoggerAndConsoleOnClient(name: string): void {
  if (!IS_CLIENT_SIDE) {
    // warn with default logger
    Monitoring.Logging.getLogger('cms.client').warn('Warn: Trying to configure client logging on the server')
    return
  }
  const fetchImpl = IS_DEPLOYED ? fetch.bind(window) : (stubFetch(() => {}) as typeof fetch)
  const felogClient: LoggingImpl.AsyncLogAppender = new LoggingImpl.PostFelogClient(
    tracking.felog.url + '/log',
    name,
    BUILD_TAG,
    GR_ENV,
    fetchImpl,
    false
  )

  const defaultSink: LoggingImpl.LogAppender = async (event: LogEvent): Promise<void> => {
    const eventWithDefaultContext = await addLoggingContext(event)
    if (!IS_DEPLOYED || GR_ENV !== 'prod' || event.level >= Logging.LogLevel.WARN) {
      LoggingImpl.ConsoleLogger.printToConsole(eventWithDefaultContext)
    }
    if (!IS_DEPLOYED || GR_ENV !== 'prod' || event.level >= Logging.LogLevel.INFO) {
      felogClient.append(eventWithDefaultContext).catch((error) => console.error('Error sending logs', error))
    }
  }

  const logger: Logging.Logger = new LoggingImpl.SimpleLogger(name, Logging.LogLevel.TRACE, defaultSink)

  LoggingImpl.LoggingConfig.configure(logger)
}

export function configureLoggerAndConsoleOnServer(name: string): void {
  if (IS_CLIENT_SIDE) {
    // warn with default logger
    Monitoring.Logging.getLogger('cms.ssr').warn('Warn: Trying to configure server logging on the client')
    return
  }

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const os = require('os')
  const defaultSink: LoggingImpl.LogAppender = (event: LogEvent): void => {
    /**
     * We write to stdout because we use `Log driver: awslogs` for this nodejs server.
     * It means that these logs are going to CloudWatch.
     * Also, we have special lamda-service which takes these logs and put them into our SumoLogic.
     * In SumoLogic you can find them by this query:
     * _sourceCategory=aws/{env}-acquisition-web/cloudwatch-logs AND _sourceHost=/ecs/{env}-cms
     */
    process.stdout.write(JSON.stringify(event) + os.EOL)
  }

  LoggingImpl.LoggingConfig.configure(new LoggingImpl.SimpleLogger(name, Logging.LogLevel.INFO, defaultSink))
  Monitoring.Logging.root.context.add({ hostname: os.hostname(), box_id: process.env.BOX_ID })
}
